<script context="module">
  // @ts-check
  export const VALID_VARIANTS = ["small", "large", "inline"];
</script>

<script>
  import Icon from "./Icon.svelte";
  import { validatePropValue } from "../utilities/logs";
  import { THEME_NAMES, getTheme } from "../utilities/themes";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  export let theme = "white";
  validatePropValue({
    componentName: "Button",
    propName: "theme",
    propValue: theme,
    validPropValues: THEME_NAMES
  });
  $: componentColorTheme = getTheme(theme)?.componentColorTheme ?? "white";

  /**
   * The variant of the link. It should be one of "small", "large", or "inline".
   * @type { "small" | "large" | "inline" }
   */
  export let variant = "small";

  validatePropValue({
    componentName: "Link",
    propName: "variant",
    propValue: variant,
    validPropValues: VALID_VARIANTS
  });

  /**
   * The URL of the link.
   * @type {string}
   */
  export let href = undefined;

  /**
   * The visible label of the link.
   * @type {string}
   */
  export let label = "";

  /**
   * Determines if the link is disabled if it is a button.
   * @type {boolean}
   */
  export let disabled = false;

  /**
   * Indicates whether the link is hidden from assistive technologies.
   * @type {boolean}
   */
  export let ariaHidden = false;

  /**
   * Determines if the link is external.
   * @type {boolean}
   */
  export let isExternal = false;

  /**
   * Indicates whether the link is currently being hovered.
   * @type {boolean}
   */
  export let isLinkHovered = false;

  /**
   * Indicates whether the arrow should hide
   * @type {boolean}
   */
  export let hideInternalLinkArrow = false;

  /**
   * Indicates whether the arrow should show on hover
   * @type {boolean}
   */
  export let showLinkArrowOnHover = false;

  /** @type { string } */
  let linkClass = "";
  export { linkClass as class };

  const baseClasses =
    "outline-2 outline-offset-4 focus-visible:outline-dashed cursor-pointer relative";

  const themeClasses = {
    white: "text-black outline-black focus-visible:border-black",
    black: "text-white outline-white focus-visible:border-white"
  };

  const variantClasses = {
    inline: "inline",
    large: "cta-lg flex items-center",
    small: "cta-md flex items-center"
  };

  const hoverClasses = {
    inline: "hover:before:w-[0px] hover:text-cobalt-blue",
    large: "",
    small: ""
  };

  const disabledClasses = disabled ? "disabled:text-grey disabled:pointer-events-none" : "";

  const iconClasses = {
    inline: "xl:w-6 xl:h-6 inline",
    large: "ml-1 lg:ml-2 lg:w-6 lg:h-6",
    small: "ml-1 xl:ml-2 xl:w-6 xl:h-6"
  };

  const arrowClasses = showLinkArrowOnHover ? "animate-opacity" : "";

  $: animationTriggerClass = isLinkHovered ? "is-link-hovered" : "";

  const getElementType = () => {
    let element;
    if (href) {
      element = "a";
    } else if (ariaHidden) {
      element = "span";
    } else {
      element = "button";
    }
    return element;
  };

  $: shouldShowArrow =
    !(variant === "inline" && !isExternal) && (isExternal || !hideInternalLinkArrow);
  $: animatedUnderlineClasses = variant !== "inline" ? "animate-underline" : "";
  $: innerSpanClasses = variant == "inline" ? "underline" : "";
</script>

<!-- Note: this duplicated block of code is necessary because the internal {#if} Svelte code causes unwanted whitespace in the link  -->
{#if shouldShowArrow || isExternal}
  <svelte:element
    this={getElementType()}
    {id}
    {href}
    target={isExternal ? "_blank" : null}
    rel={isExternal ? "noopener noreferrer" : null}
    data-sveltekit-reload={href ? true : undefined}
    disabled={href ? undefined : disabled}
    aria-hidden={ariaHidden}
    class="link {baseClasses} {variantClasses[variant]} {themeClasses[componentColorTheme]} 
  {disabledClasses} {linkClass} {hoverClasses[variant]} {animationTriggerClass}"
    role={undefined}
    on:click
    {...$$restProps}
  >
    <!-- This extra span is required here to encapsulate the child animation  -->
    <span>
      <span class={animatedUnderlineClasses}>
        <span class={innerSpanClasses}>
          <slot>{label}</slot>
        </span>
        {#if shouldShowArrow}
          <span class="inline whitespace-nowrap {arrowClasses}">
            <span class="inline-block">
              <Icon
                iconClass={iconClasses[variant]}
                name={isExternal ? "arrowUpRight" : "arrowRight"}
              />
            </span>
          </span>
        {/if}
      </span>
    </span>
    {#if isExternal}
      <span class="sr-only">(Opens in a new tab)</span>
    {/if}
  </svelte:element>
{:else}
  <svelte:element
    this={getElementType()}
    {id}
    {href}
    data-sveltekit-reload={href ? true : undefined}
    disabled={href ? undefined : disabled}
    aria-hidden={ariaHidden}
    class="link {baseClasses} {variantClasses[variant]} {themeClasses[componentColorTheme]} 
  {disabledClasses} {linkClass} {hoverClasses[variant]} {animationTriggerClass}"
    role={undefined}
    on:click
    {...$$restProps}
  >
    <!-- This extra span is required here to encapsulate the child animation  -->
    <span>
      <span class={animatedUnderlineClasses}>
        <span class={innerSpanClasses}>
          <slot>{label}</slot>
        </span>
      </span>
    </span>
  </svelte:element>
{/if}

<style>
  .animate-underline {
    text-decoration: none;
    background-image: linear-gradient(currentColor, currentColor);
    background-repeat: no-repeat;
    background-position: bottom left;
    background-size: 0% 2px;
    transition: background-size 300ms ease-in-out;
  }

  .animate-opacity {
    opacity: 0;
    transition: opacity 300ms ease-in-out;
    margin-left: -5px;
  }

  .is-link-hovered .animate-underline,
  .link:hover .animate-underline {
    background-size: 100% 2px;
    color: inherit;
  }

  .link:hover .animate-opacity,
  .link:focus .animate-opacity {
    opacity: 1;
  }
</style>
